import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from './Button';

// Define ButtonProps interface since we can't import it directly from Button
interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outlined' | 'outline' | 'text' | 'ghost' | 'danger';
  color?: 'primary' | 'secondary' | 'danger' | 'red' | 'gray';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  loading?: boolean;
  loadingText?: string;
}

interface AlertDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmButtonProps?: Partial<ButtonProps>;
  cancelButtonProps?: Partial<ButtonProps>;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
  open,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  confirmButtonProps,
  cancelButtonProps
}) => {
  const { t } = useTranslation();
  const defaultConfirmText = confirmText || t('common.actions.confirm');
  const defaultCancelText = cancelText || t('common.actions.cancel');
  
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 animate-fade-in">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h2>
        
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          {message}
        </p>
        
        <div className="flex justify-end space-x-3">
          <Button
            variant="text"
            onClick={onCancel}
            {...cancelButtonProps}
          >
            {defaultCancelText}
          </Button>
          
          <Button
            variant="primary"
            onClick={onConfirm}
            {...confirmButtonProps}
          >
            {defaultConfirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlertDialog;